import { Global } from '@emotion/react';
import { Box, Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { JobDescriptionPopover, ResumePopover } from '../JobAndResumePopover';
import AdaptiveCardRenderer from './AdaptiveCardRenderer/AdaptiveCardRenderer';
import FeedbackHeader from './FeedbackHeader/FeedbackHeader';
import MetaDataCard from './MetaDataCard/MetaDataCard';
import { adaptiveCardCss } from './styles';

const headerHeightWithMargin = '96px';
const classes = {
	mainContainer: {
		display: 'flex',
		flexDirection: { xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' },
		marginX: { xs: '0', sm: '0', md: '16px', lg: '16px', xl: '16px' },
		marginTop: { xs: '0', sm: '0', md: '16px', lg: '16px', xl: '16px' },
		maxHeight: `calc(100vh - ${headerHeightWithMargin})`,
		overflow: { xs: 'auto', sm: 'auto', md: 'none', lg: 'none', xl: 'none' },
	},
	cardContainer: {
		flexBasis: { xs: 'unset', sm: 'unset', md: '20%', lg: '20%', xl: '20%' },
		marginX: { xs: '0', sm: '0', md: '16px', lg: '16px', xl: '16px' },
		display: { md: 'flex', lg: 'flex' },
		flexDirection: { md: 'row-reverse', lg: 'row-reverse' },
		maxHeight: { xs: 'unset', sm: 'unset', md: '440px', lg: '440px', xl: '440px' },
		overflow: { xs: 'unset', sm: 'unset', md: 'auto', lg: 'auto', xl: 'auto' },
	},
	feedbackDetailsCard: {
		padding: (theme) => theme.spacing(2),
		marginBottom: (theme) => theme.spacing(1),
		backgroundColor: 'background.paper',
		height: '100%,',
		flexBasis: { sx: 'unset', sm: 'unset', md: '60%', lg: '60%', xl: '60%' },
		overflow: { xs: 'none', sm: 'none', md: 'auto', lg: 'auto', xl: 'auto' },
	},
};

const hasValue = (array, value) => array.includes(value);
const isInProgress = (state, noChanges) => state !== 'IN_PROGRESS' && noChanges;

const FeedbackForm = ({
	cardDetails,
	initialResponses,
	responses,
	formCard,
	title,
	setResponses,
	handleOnSaveButtonClick,
	handleOnSubmitButtonClick,
	handlePrint = null,
	isPending,
	feedbackRequestStatus = null,
	containerClass = classes.mainContainer,
	cardContainerClass = classes.cardContainer,
	formContainerClass = classes.feedbackDetailsCard,
	theme = 'light',
	disableEdit = false,
	isATS = false,
	handleChange,
}) => {
	const { t } = useTranslation(['candidate-feedback', 'common']);

	const [showJobDescPopover, setShowJobDescPopover] = useState(false);
	const [showResumePopover, setShowResumePopover] = useState(false);

	const { submitButtonTitle, saveButtonTitle } = useMemo(
		() => ({
			requiredText: t('required'),
			submitButtonTitle: t('submit'),
			saveButtonTitle: t('save_draft'),
		}),
		[t],
	);

	return (
		<>
			<Box sx={containerClass} tabIndex="-1">
				<Global styles={adaptiveCardCss} />
				<Box sx={cardContainerClass} tabIndex="-1">
					<MetaDataCard
						cardDetails={cardDetails}
						showJobDescPopover={showJobDescPopover}
						setShowJobDescPopover={setShowJobDescPopover}
						showResumePopover={showResumePopover}
						setShowResumePopover={setShowResumePopover}
						isATS={isATS}
					/>
				</Box>
				<Box sx={formContainerClass} data-testid="feedback-details-card" tabIndex="-1">
					<FeedbackHeader
						title={title}
						feedbackState={cardDetails.feedbackState}
						handlePrint={cardDetails.feedbackState === 'SUBMITTED' ? handlePrint : null}
					/>
					<AdaptiveCardRenderer
						adaptiveCardJson={formCard}
						disableEdit={disableEdit}
						setResponses={setResponses}
						theme={theme}
						feedbackRequestStatus={feedbackRequestStatus}
						handleChange={handleChange}
					/>
					<Box display="flex" flexDirection="row" justifyContent="flex-end" marginBottom={3}>
						<Button
							variant="text"
							disabled={hasValue([disableEdit, isPending], true)}
							sx={{ marginX: '24px' }}
							data-testid="save-form-button"
							onClick={handleOnSaveButtonClick}
						>
							{saveButtonTitle}
						</Button>
						<Button
							variant="contained"
							disabled={hasValue(
								[
									disableEdit,
									isPending,
									isInProgress(cardDetails.feedbackState, initialResponses === responses),
								],
								true,
							)}
							data-testid="submit-form-button"
							onClick={handleOnSubmitButtonClick}
						>
							{submitButtonTitle}
						</Button>
					</Box>
				</Box>
			</Box>
			<JobDescriptionPopover
				isATS={isATS}
				customerId={cardDetails.customerId}
				jobId={cardDetails.jobId}
				showJobDescPopover={showJobDescPopover}
				setShowJobDescPopover={setShowJobDescPopover}
			/>
			<ResumePopover
				isATS={isATS}
				customerId={cardDetails.customerId}
				intervieweeId={cardDetails.intervieweeId}
				showResumePopover={showResumePopover}
				setShowResumePopover={setShowResumePopover}
			/>
		</>
	);
};

export default FeedbackForm;
